import { useState, useEffect } from 'react';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useUserPreferences, defaultPreferences } from '@/providers/UserPreferencesProvider';
import { useDemoSession } from '@/providers/DemoSessionProvider';
import { Save, X, Layout, Eye, Keyboard, Home, Layers } from 'lucide-react';

interface UserPreferencesPageProps {
  onNavigate?: (page: string) => void;
}

// Navigation items for the landing page dropdown
const landingPageOptions = [
  { id: 'dashboard', name: 'Dashboard (default)' },
  { id: 'projects', name: 'Projects' },
  { id: 'portfolio', name: 'Portfolio' },
  { id: 'utilisation', name: 'Utilisation' },
  { id: 'wip-reporting', name: 'WIP Reporting' },
  { id: 'profitability', name: 'Profitability' },
  { id: 'budgets', name: 'Budgets & Variance' },
  { id: 'resources', name: 'Resource Allocation' },
  { id: 'gantt', name: 'Gantt Charts' },
];

// Helper function to get greeting based on time of day
const getGreeting = () => {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 18) return 'Good Afternoon';
  return 'Good Evening';
};

export function UserPreferencesPage({ onNavigate }: UserPreferencesPageProps) {
  const { preferences, updatePreferences } = useUserPreferences();
  const { userName } = useDemoSession();
  const [localSidebarPosition, setLocalSidebarPosition] = useState(preferences.sidebarPosition);
  const [localSidebarStyle, setLocalSidebarStyle] = useState(preferences.sidebarStyle);
  const [localShowWelcomeDialog, setLocalShowWelcomeDialog] = useState(preferences.showWelcomeDialog);
  const [localDefaultLandingPage, setLocalDefaultLandingPage] = useState(preferences.defaultLandingPage);
  const [localRememberLastPage, setLocalRememberLastPage] = useState(preferences.rememberLastPage);
  const [localDisplayDensity, setLocalDisplayDensity] = useState(preferences.displayDensity);
  const [localKeyboardShortcutsEnabled, setLocalKeyboardShortcutsEnabled] = useState(preferences.keyboardShortcutsEnabled);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [showUnsavedDialog, setShowUnsavedDialog] = useState(false);
  const [showResetDialog, setShowResetDialog] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState<string | null>(null);

  // Check if there are unsaved changes
  useEffect(() => {
    const hasChanges = localSidebarPosition !== preferences.sidebarPosition ||
      localSidebarStyle !== preferences.sidebarStyle ||
      localShowWelcomeDialog !== preferences.showWelcomeDialog ||
      localDefaultLandingPage !== preferences.defaultLandingPage ||
      localRememberLastPage !== preferences.rememberLastPage ||
      localDisplayDensity !== preferences.displayDensity ||
      localKeyboardShortcutsEnabled !== preferences.keyboardShortcutsEnabled;
    setHasUnsavedChanges(hasChanges);
  }, [
    localSidebarPosition, 
    localSidebarStyle, 
    localShowWelcomeDialog, 
    localDefaultLandingPage,
    localRememberLastPage,
    localDisplayDensity,
    localKeyboardShortcutsEnabled,
    preferences.sidebarPosition, 
    preferences.sidebarStyle, 
    preferences.showWelcomeDialog,
    preferences.defaultLandingPage,
    preferences.rememberLastPage,
    preferences.displayDensity
  ]);

  const handleSave = () => {
    updatePreferences({
      sidebarPosition: localSidebarPosition,
      sidebarStyle: localSidebarStyle,
      showWelcomeDialog: localShowWelcomeDialog,
      defaultLandingPage: localDefaultLandingPage,
      rememberLastPage: localRememberLastPage,
      displayDensity: localDisplayDensity,
      keyboardShortcutsEnabled: localKeyboardShortcutsEnabled,
    });
    setHasUnsavedChanges(false);
  };

  const handleDiscard = () => {
    if (hasUnsavedChanges) {
      setShowUnsavedDialog(true);
    }
  };

  const confirmDiscard = () => {
    setLocalSidebarPosition(preferences.sidebarPosition);
    setLocalSidebarStyle(preferences.sidebarStyle);
    setLocalShowWelcomeDialog(preferences.showWelcomeDialog);
    setLocalDefaultLandingPage(preferences.defaultLandingPage);
    setLocalRememberLastPage(preferences.rememberLastPage);
    setLocalDisplayDensity(preferences.displayDensity);
    setLocalKeyboardShortcutsEnabled(preferences.keyboardShortcutsEnabled);
    setHasUnsavedChanges(false);
    setShowUnsavedDialog(false);
    
    // If there was a pending navigation, execute it
    if (pendingNavigation && onNavigate) {
      onNavigate(pendingNavigation);
      setPendingNavigation(null);
    }
  };

  const cancelDiscard = () => {
    setShowUnsavedDialog(false);
    setPendingNavigation(null);
  };

  const handleResetToDefault = () => {
    setShowResetDialog(true);
  };

  const confirmReset = () => {
    // Reset all preferences to default and save immediately
    updatePreferences(defaultPreferences);
    
    // Update local state to match
    setLocalSidebarPosition(defaultPreferences.sidebarPosition);
    setLocalSidebarStyle(defaultPreferences.sidebarStyle);
    setLocalShowWelcomeDialog(defaultPreferences.showWelcomeDialog);
    setLocalDefaultLandingPage(defaultPreferences.defaultLandingPage);
    setLocalRememberLastPage(defaultPreferences.rememberLastPage);
    setLocalDisplayDensity(defaultPreferences.displayDensity);
    setLocalKeyboardShortcutsEnabled(defaultPreferences.keyboardShortcutsEnabled);
    setHasUnsavedChanges(false);
    setShowResetDialog(false);
  };

  const cancelReset = () => {
    setShowResetDialog(false);
  };

  // Intercept navigation attempts when there are unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  return (
    <>
      <div className="max-w-4xl mx-auto space-y-8 pb-32">
        {/* Header */}
        <div className="space-y-2">
          <h2 className="text-3xl font-bold tracking-tight">{getGreeting()}, {userName}</h2>
          <p className="text-muted-foreground">
            Customise Access Projects to fit your preferences
          </p>
        </div>

        {/* Appearance Section */}
        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Layout className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Appearance</h3>
              <p className="text-sm text-muted-foreground">Customize the look and feel of your interface</p>
            </div>
          </div>
          
          <div className="rounded-lg border bg-card">
            <div className="divide-y">
              {/* Sidebar Position */}
              <div className="flex items-center justify-between p-4">
                <div className="space-y-0.5 flex-1">
                  <Label htmlFor="sidebar-position" className="text-sm font-medium">
                    Sidebar Position
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Choose which side the navigation sidebar appears
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${localSidebarPosition === 'left' ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                    Left
                  </span>
                  <Switch
                    id="sidebar-position"
                    checked={localSidebarPosition === 'right'}
                    onCheckedChange={(checked) => setLocalSidebarPosition(checked ? 'right' : 'left')}
                  />
                  <span className={`text-xs ${localSidebarPosition === 'right' ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                    Right
                  </span>
                </div>
              </div>

              {/* Sidebar Theme */}
              <div className="flex items-center justify-between p-4">
                <div className="space-y-0.5 flex-1">
                  <Label htmlFor="sidebar-theme" className="text-sm font-medium">
                    Sidebar Theme
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Select between classic Legacy or modern Evo design
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${localSidebarStyle === 'default' ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                    Legacy
                  </span>
                  <Switch
                    id="sidebar-theme"
                    checked={localSidebarStyle === 'evo'}
                    onCheckedChange={(checked) => setLocalSidebarStyle(checked ? 'evo' : 'default')}
                  />
                  <span className={`text-xs ${localSidebarStyle === 'evo' ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                    Evo
                  </span>
                </div>
              </div>

              {/* Display Density */}
              <div className="flex items-center justify-between p-4">
                <div className="space-y-0.5 flex-1">
                  <Label htmlFor="display-density" className="text-sm font-medium">
                    Display Density
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Adjust spacing and sizing for your preferred information density
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs ${localDisplayDensity === 'comfortable' ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                    Comfortable
                  </span>
                  <Switch
                    id="display-density"
                    checked={localDisplayDensity === 'compact'}
                    onCheckedChange={(checked) => setLocalDisplayDensity(checked ? 'compact' : 'comfortable')}
                  />
                  <span className={`text-xs ${localDisplayDensity === 'compact' ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                    Compact
                  </span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Navigation Section */}
        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Home className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Navigation</h3>
              <p className="text-sm text-muted-foreground">Control your navigation and startup behavior</p>
            </div>
          </div>
          
          <div className="rounded-lg border bg-card">
            <div className="divide-y">
              {/* Default Landing Page */}
              <div className="flex items-center justify-between p-4">
                <div className="space-y-0.5 flex-1">
                  <Label htmlFor="landing-page" className="text-sm font-medium">
                    Default Landing Page
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Choose which page opens when you log in
                  </p>
                </div>
                <Select
                  value={localDefaultLandingPage}
                  onValueChange={setLocalDefaultLandingPage}
                >
                  <SelectTrigger id="landing-page" className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {landingPageOptions.map((option) => (
                      <SelectItem key={option.id} value={option.id}>
                        {option.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Remember Last Page */}
              <div className="flex items-center justify-between p-4">
                <div className="space-y-0.5 flex-1">
                  <Label htmlFor="remember-page" className="text-sm font-medium cursor-pointer">
                    Remember Last Visited Page
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Automatically return to your last visited page on login (overrides default landing page)
                  </p>
                </div>
                                <Checkbox
                                  id="remember-page"
                                  checked={localRememberLastPage}
                                  onCheckedChange={(checked) => setLocalRememberLastPage(checked === true)}
                                />
              </div>
            </div>
          </div>
        </section>

        {/* Accessibility Section */}
        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Keyboard className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Accessibility</h3>
              <p className="text-sm text-muted-foreground">Keyboard shortcuts and interaction preferences</p>
            </div>
          </div>
          
          <div className="rounded-lg border bg-card">
            <div className="divide-y">
              {/* Keyboard Shortcuts */}
              <div className="flex items-center justify-between p-4">
                <div className="space-y-0.5 flex-1">
                  <Label htmlFor="keyboard-shortcuts" className="text-sm font-medium cursor-pointer">
                    Keyboard Shortcuts
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Enable keyboard shortcuts for faster navigation and accessibility
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Press <kbd className="px-1.5 py-0.5 text-[10px] font-semibold bg-muted border border-border rounded">Ctrl+/</kbd> to view all shortcuts
                  </p>
                </div>
                                <Checkbox
                                  id="keyboard-shortcuts"
                                  checked={localKeyboardShortcutsEnabled}
                                  onCheckedChange={(checked) => setLocalKeyboardShortcutsEnabled(checked === true)}
                                />
              </div>
            </div>
          </div>
        </section>

        {/* General Section */}
        <section className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Eye className="h-5 w-5 text-primary" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">General</h3>
              <p className="text-sm text-muted-foreground">General application preferences</p>
            </div>
          </div>
          
          <div className="rounded-lg border bg-card">
            <div className="divide-y">
              {/* Welcome Dialog */}
              <div className="flex items-center justify-between p-4">
                <div className="space-y-0.5 flex-1">
                  <Label htmlFor="show-welcome" className="text-sm font-medium cursor-pointer">
                    Show Welcome Dialog
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Display the welcome message when the application starts
                  </p>
                </div>
                                <Checkbox
                                  id="show-welcome"
                                  checked={localShowWelcomeDialog}
                                  onCheckedChange={(checked) => setLocalShowWelcomeDialog(checked === true)}
                                />
              </div>
            </div>
          </div>
        </section>
      </div>

      {/* Action Buttons - Fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 border-t bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 z-50">
        <div className="max-w-4xl mx-auto flex justify-end items-center gap-3 p-4">
          <Button
            variant="secondary"
            onClick={handleResetToDefault}
          >
            Reset to Default
          </Button>
          <Button
            variant="destructive"
            onClick={handleDiscard}
            disabled={!hasUnsavedChanges}
            className="gap-2"
          >
            <X className="h-4 w-4" />
            Discard
          </Button>
          <Button
            onClick={handleSave}
            disabled={!hasUnsavedChanges}
            className="gap-2"
          >
            <Save className="h-4 w-4" />
            Save Changes
          </Button>
        </div>
      </div>

      {/* Unsaved Changes Dialog */}
      <AlertDialog open={showUnsavedDialog} onOpenChange={setShowUnsavedDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Unsaved Changes</AlertDialogTitle>
            <AlertDialogDescription>
              You have unsaved changes to your preferences. If you discard them, your changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelDiscard}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmDiscard} className="bg-destructive hover:bg-destructive/90">
              Discard Changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reset to Default Dialog */}
      <AlertDialog open={showResetDialog} onOpenChange={setShowResetDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to Default Settings?</AlertDialogTitle>
            <AlertDialogDescription>
              This will immediately reset all your preferences to their default values. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={cancelReset}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction onClick={confirmReset}>
              Confirm Reset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
